import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ShieldCheck, AlertTriangle, Download, Copy, Printer, Loader2, Key } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAppConfig } from "../lib/AppConfigContext";
import { toast } from "sonner";
import api from "../lib/api";
import { confirmKeys } from "../lib/api";

export default function GenerateKeysPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appName } = useAppConfig();
  const [keyData, setKeyData] = useState(null);
  const [generating, setGenerating] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    const entityId = localStorage.getItem("entity_id");
    if (!entityId) {
      navigate("/login");
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    generateKeys();
  }, []);

  const generateKeys = async () => {
    try {
      const res = await api.post("/entities/generate-keys");
      setKeyData(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail === "Keys already generated") {
        // Keys confirmed — go to dashboard
        navigate("/dashboard");
        return;
      }
      setError(detail || t("common.error"));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadKey = () => {
    if (!keyData?.entity_key_part) return;
    const blob = new Blob(
      [keyData.entity_key_part],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(appName || "votasegur").replace(/\s+/g, "_")}_${t("register.privateKeyFilename")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("register.downloadKey"));
  };

  const handleCopyKey = () => {
    if (!keyData?.entity_key_part) return;
    navigator.clipboard.writeText(keyData.entity_key_part);
    toast.success(t("common.copied"));
  };

  const handlePrintKey = () => {
    if (!keyData?.entity_key_part) return;
    const w = window.open("", "_blank");
    w.document.write(`<pre style="font-size:12px;white-space:pre-wrap;word-break:break-all;">${keyData.entity_key_part}</pre>`);
    w.document.close();
    w.print();
  };

  const handleContinue = () => {
    setShowConfirm(true);
  };

  const handleConfirmAccept = async () => {
    setConfirming(true);
    try {
      await confirmKeys();
      setShowConfirm(false);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setConfirming(false);
    }
  };

  if (generating) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <Loader2 className="h-12 w-12 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-lg text-slate-600">{t("register.generatingKeys")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg text-slate-600">{error}</p>
          <Button className="mt-4" onClick={() => navigate("/login")}>{t("register.goToLogin")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="generate-keys-page">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Key className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">{t("register.privateKeyGenerated")}</h1>
            <p className="text-sm text-slate-500 mt-1">{t("register.verificationSuccessDesc")}</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm font-semibold text-amber-800">{t("register.privateKeyWarning")}</p>
            </div>
            <div className="bg-white border border-amber-300 rounded p-3 font-mono text-xs break-all max-h-32 overflow-y-auto" data-testid="entity-key-display">
              {keyData?.entity_key_part}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={handleCopyKey} data-testid="copy-key-btn">
              <Copy className="h-4 w-4 mr-1" /> {t("common.copy")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadKey} data-testid="download-key-btn">
              <Download className="h-4 w-4 mr-1" /> {t("register.downloadKey")}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrintKey} data-testid="print-key-btn">
              <Printer className="h-4 w-4 mr-1" /> {t("common.print")}
            </Button>
          </div>

          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleContinue}
            data-testid="continue-to-dashboard-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            {t("register.keySavedContinue")}
          </Button>
        </div>
      </motion.div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="confirm-key-saved-dialog">
          <div className="fixed inset-0 bg-black/80" />
          <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-lg p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2">{t("register.privateKeyGenerated")}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line mb-6">
              {t("register.confirmSaveKey")}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={confirming}
                data-testid="confirm-key-cancel"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleConfirmAccept}
                disabled={confirming}
                data-testid="confirm-key-accept"
              >
                {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {t("common.confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
